import { DocumentFilters, DocumentsMeta, ProductOption } from './types';
import { EMPTY_FILTERS, TYPE_OPTIONS } from './constants';

/** Даты MSSQL приходят как «локальное время в UTC» - выводим без сдвига часового пояса */
export function fmtDateTime(iso?: string | null, withYear = true): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('ru-RU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    ...(withYear ? { year: '2-digit' } : {}),
    hour: '2-digit',
    minute: '2-digit'
  });
}

/** Время журнала действий (MySQL «YYYY-MM-DD HH:mm:ss», уже местное) - в том же виде, что fmtDateTime */
export function fmtLocalDateTime(value?: string | null): string {
  const m = value ? /^\d{2}(\d{2})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value) : null;
  return m ? `${m[3]}.${m[2]}.${m[1]}, ${m[4]}:${m[5]}` : '-';
}

export function fmtDate(iso?: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('ru-RU', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: '2-digit' });
}

export function fmtMoney(value?: number | null, digits = 0): string {
  if (value === null || value === undefined) return '-';
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: digits })} ₸`;
}

export function fmtNumber(value?: number | null): string {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

/** Склонение: plural(5, 'день', 'дня', 'дней') */
export function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}

export function daysLabel(n: number): string {
  return `${n} ${plural(n, 'день', 'дня', 'дней')}`;
}

/** Локальная дата в формате yyyy-mm-dd со сдвигом в днях */
export function isoDay(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Количество активных фильтров (без поиска - он виден в строке поиска) */
export function countActiveFilters(f: DocumentFilters): number {
  let n = 0;
  if (f.type) n++;
  if (f.statuses.length) n++;
  if (f.pharmacy.length) n++;
  if (f.sender.length) n++;
  if (f.recipient.length) n++;
  if (f.region.length) n++;
  if (f.route) n++;
  if (f.marking) n++;
  if (f.createdFrom || f.createdTo) n++;
  if (f.updatedFrom || f.updatedTo) n++;
  if (f.stuckDays) n++;
  if (f.warn) n++;
  if (f.hidden !== 'all') n++;
  if (f.author) n++;
  if (f.product || f.products.length) n++;
  return n;
}

export const hasAnyFilter = (f: DocumentFilters): boolean => countActiveFilters(f) > 0 || Boolean(f.search);

/** Фильтры → параметры запроса к API (пустые значения отбрасываются) */
export function filtersToParams(f: DocumentFilters): Record<string, string> {
  const p: Record<string, string> = {};
  const put = (k: string, v: string) => { if (v) p[k] = v; };
  put('search', f.search.trim());
  put('type', f.type);
  put('status', f.statuses.join(','));
  put('pharmacy', f.pharmacy.join(','));
  put('sender', f.sender.join(','));
  put('recipient', f.recipient.join(','));
  put('region', f.region.join(','));
  put('route', f.route);
  put('marking', f.marking);
  put('createdFrom', f.createdFrom);
  put('createdTo', f.createdTo);
  put('updatedFrom', f.updatedFrom);
  put('updatedTo', f.updatedTo);
  put('stuckDays', f.stuckDays);
  if (f.warn) p.warn = '1';
  if (f.hidden !== 'all') p.hidden = f.hidden;
  put('author', f.author.trim());
  put('productId', f.products.map(x => x.id).join(','));
  put('product', f.product.trim());
  return p;
}

/** Безопасное восстановление фильтров из localStorage: неизвестные/битые поля заменяются значениями по умолчанию */
export function sanitizeFilters(raw: unknown): DocumentFilters {
  const src = (raw && typeof raw === 'object' ? raw : {}) as Partial<DocumentFilters>;
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const arr = (v: unknown) => (Array.isArray(v) ? v.filter(x => typeof x === 'string') as string[] : []);
  return {
    ...EMPTY_FILTERS,
    search: str(src.search),
    type: src.type === '4' || src.type === '5' ? src.type : '',
    statuses: arr(src.statuses),
    pharmacy: arr(src.pharmacy),
    sender: arr(src.sender),
    recipient: arr(src.recipient),
    region: arr(src.region),
    route: src.route === 'direct' || src.route === 'via' ? src.route : '',
    marking: src.marking === 'marked' || src.marking === 'unmarked' ? src.marking : '',
    createdFrom: str(src.createdFrom),
    createdTo: str(src.createdTo),
    updatedFrom: str(src.updatedFrom),
    updatedTo: str(src.updatedTo),
    stuckDays: str(src.stuckDays),
    warn: Boolean(src.warn),
    hidden: src.hidden === 'exclude' || src.hidden === 'only' ? src.hidden : 'all',
    author: str(src.author),
    product: str(src.product),
    products: Array.isArray(src.products)
      ? src.products
        .filter(x => x && typeof x === 'object' && typeof (x as ProductOption).id === 'string')
        .map(x => ({
          id: (x as ProductOption).id, name: str((x as ProductOption).name), code: str((x as ProductOption).code),
          barcode: str((x as ProductOption).barcode), docs: Number((x as ProductOption).docs) || 0
        }))
        .slice(0, 20)
      : []
  };
}

export type FilterLabelKey =
  | 'search' | 'type' | 'groups' | 'warn' | 'pharmacy' | 'sender' | 'recipient' | 'region' | 'status' | 'route'
  | 'marking' | 'created' | 'updated' | 'stuck' | 'hidden' | 'author' | 'product';

/** Подписи применённых фильтров: чипы под фильтрами списка и сведения в окне выгрузки */
export function filterLabels(f: DocumentFilters, meta: Pick<DocumentsMeta, 'pharmacies' | 'statuses' | 'groups'>): Array<{ key: FilterLabelKey; label: string }> {
  const out: Array<{ key: FilterLabelKey; label: string }> = [];
  const put = (key: FilterLabelKey, label: string) => out.push({ key, label });
  const pharmacyNames: Record<string, string> = {};
  meta.pharmacies.forEach(p => { pharmacyNames[p.guid] = p.name; });
  const names = (guids: string[]) =>
    (guids.length <= 2 ? guids.map(g => pharmacyNames[g] || '…').join(', ') : `${guids.length} аптек/складов`);
  const numeric = f.statuses.filter(s => /^\d+$/.test(s));
  const groups = f.statuses.filter(s => !/^\d+$/.test(s));

  if (f.search.trim()) put('search', `Поиск: «${f.search.trim()}»`);
  if (f.type) put('type', `Тип: ${TYPE_OPTIONS.find(o => o.value === f.type)?.label || f.type}`);
  if (groups.length) put('groups', `Статусы: ${groups.map(g => meta.groups.find(x => x.id === g)?.name || g).join(', ')}`);
  if (f.warn) put('warn', 'Зависшие');
  if (f.pharmacy.length) put('pharmacy', `Участник: ${names(f.pharmacy)}`);
  if (f.sender.length) put('sender', `Откуда: ${names(f.sender)}`);
  if (f.recipient.length) put('recipient', `Куда: ${names(f.recipient)}`);
  if (f.region.length) put('region', `Регион: ${f.region.length <= 2 ? f.region.join(', ') : `${f.region.length} рег.`}`);
  if (numeric.length) {
    const label = numeric.length <= 2
      ? numeric.map(s => meta.statuses.find(x => String(x.id) === s)?.name || s).join(', ')
      : `${numeric.length} ${plural(numeric.length, 'статус', 'статуса', 'статусов')}`;
    put('status', `Статус: ${label}`);
  }
  if (f.route) put('route', f.route === 'via' ? 'Через склад' : 'Прямое');
  if (f.marking) put('marking', f.marking === 'marked' ? 'Маркировка: КМ' : 'Маркировка: без КМ');
  if (f.createdFrom || f.createdTo) put('created', `Создан: ${f.createdFrom || '…'} - ${f.createdTo || '…'}`);
  if (f.updatedFrom || f.updatedTo) put('updated', `Обновлён: ${f.updatedFrom || '…'} - ${f.updatedTo || '…'}`);
  if (f.stuckDays) put('stuck', `Без изменений ≥ ${daysLabel(Number(f.stuckDays))}`);
  if (f.hidden !== 'all') put('hidden', f.hidden === 'only' ? 'Только скрытые' : 'Без скрытых');
  if (f.author) put('author', `Автор: ${f.author}`);
  if (f.product || f.products.length) {
    const parts = [
      ...f.products.slice(0, 2).map(p => p.name),
      ...(f.products.length > 2 ? [`ещё ${f.products.length - 2}`] : []),
      ...(f.product ? [`«${f.product}»`] : [])
    ];
    put('product', `Товар: ${parts.join(', ')}`);
  }
  return out;
}

export function downloadBlob(filename: string, blob: Blob): void {
  const url = window.URL.createObjectURL(blob);
  const link = window.document.createElement('a');
  link.href = url;
  link.download = filename;
  window.document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export function apiError(err: any, fallback: string): string {
  return err?.response?.data?.message || fallback;
}

/** Ошибка запроса с responseType 'blob': тело ответа приходит Blob'ом, сообщение нужно вычитать из JSON */
export async function apiBlobError(err: any, fallback: string): Promise<string> {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.message) return String(parsed.message);
    } catch {
      return fallback;
    }
  }
  return apiError(err, fallback);
}
